import React, { useState, useEffect } from 'react';
import { Package, CalendarClock, X, ChevronLeft } from 'lucide-react';
import {
  OFFRE_KIND,
  PRESTATION_MODE,
  resolveOffreCreateDestination,
} from '../../utils/offreCreateRouting';

/**
 * Picker création Offre O1 — 1 choix principal + sous-choix Prestation.
 * N’ouvre aucun formulaire métier tant qu’une destination n’est pas résolue.
 */
export default function OffreCreatePicker({
  isOpen,
  onClose,
  onConfirm,
}) {
  const [step, setStep] = useState('kind'); // 'kind' | 'prestationMode'
  const [kind, setKind] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('kind');
      setKind(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const confirmDestination = (nextKind, prestationMode = null) => {
    const destination = resolveOffreCreateDestination({
      kind: nextKind,
      prestationMode,
    });
    if (!destination) return;
    onConfirm?.(destination);
  };

  const handleSelectProduit = () => {
    confirmDestination(OFFRE_KIND.PRODUIT);
  };

  const handleSelectPrestation = () => {
    setKind(OFFRE_KIND.PRESTATION);
    setStep('prestationMode');
  };

  const handleBack = () => {
    setStep('kind');
    setKind(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-gray-500/70"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="offre-create-picker-title"
        className="relative bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-5 sm:p-6 space-y-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {step === 'prestationMode' && (
              <button
                type="button"
                onClick={handleBack}
                className="mb-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="h-4 w-4" />
                Retour
              </button>
            )}
            <h3
              id="offre-create-picker-title"
              className="text-lg font-semibold text-gray-900"
            >
              {step === 'kind'
                ? 'Ajouter une offre'
                : 'Comment proposer cette prestation ?'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {step === 'kind'
                ? 'Choisissez ce que vous proposez à vos clients.'
                : 'Une seule option : réservation en ligne ou présentation sans créneau.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === 'kind' && (
          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              data-offre-choice="produit"
              onClick={handleSelectProduit}
              className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left transition min-h-[72px]"
            >
              <Package className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              <span>
                <span className="block font-medium text-gray-900">Produit</span>
                <span className="text-sm text-gray-500">
                  Un article ou une marchandise à vendre
                </span>
              </span>
            </button>
            <button
              type="button"
              data-offre-choice="prestation"
              onClick={handleSelectPrestation}
              className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left transition min-h-[72px]"
            >
              <CalendarClock className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              <span>
                <span className="block font-medium text-gray-900">Prestation</span>
                <span className="text-sm text-gray-500">
                  Un soin, rendez-vous, cours, activité ou service
                </span>
              </span>
            </button>
          </div>
        )}

        {step === 'prestationMode' && kind === OFFRE_KIND.PRESTATION && (
          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              data-offre-choice="reservable"
              onClick={() =>
                confirmDestination(
                  OFFRE_KIND.PRESTATION,
                  PRESTATION_MODE.RESERVABLE
                )
              }
              className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left transition min-h-[72px]"
            >
              <CalendarClock className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              <span>
                <span className="block font-medium text-gray-900">
                  Réservable en ligne
                </span>
                <span className="text-sm text-gray-500">
                  Le client choisit un créneau ou un rendez-vous
                </span>
              </span>
            </button>
            <button
              type="button"
              data-offre-choice="sans-reservation"
              onClick={() =>
                confirmDestination(
                  OFFRE_KIND.PRESTATION,
                  PRESTATION_MODE.SANS_RESERVATION
                )
              }
              className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left transition min-h-[72px]"
            >
              <Package className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              <span>
                <span className="block font-medium text-gray-900">
                  Sans réservation en ligne
                </span>
                <span className="text-sm text-gray-500">
                  Présentée sur Clickorico, prise de rendez-vous hors ligne / sur
                  demande
                </span>
              </span>
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="w-full py-2.5 text-sm text-gray-600 hover:text-gray-900"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}
